import { forwardRef, useEffect, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import { TextWithTooltip } from '@pbt/pbt-ui-components'
import {
  LanguageUtils,
  NumberUtils,
  type SpeciesConstant,
  useFields,
} from '@pbt/pbt-ui-components'

import ActiveStateSwitch from '~/components/common/ActiveStateSwitch'
import InfoList from '~/components/common/InfoList'
import TaxRateSelect from '~/components/common/inputs/TaxRateSelect'
import PuiSwitch from '~/components/common/PuiSwitch'
import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'
import type {
  BasePrice,
  DataHandleWithUnsavedChanges,
  Price,
  UnsavedPrice,
} from '~/types'
import { isFieldValuesChanged } from '~/utils'
import { useGetFormattedPriceUnit } from '~/utils/priceUtils'

export interface ReadOnlyPriceProps {
  availableSpecies: SpeciesConstant[]
  className?: string
  price: BasePrice
}

export interface ReadOnlyPriceHandle extends DataHandleWithUnsavedChanges<UnsavedPrice> {}

const ReadOnlyPrice = forwardRef<ReadOnlyPriceHandle, ReadOnlyPriceProps>(
  function ReadOnlyPrice({ availableSpecies, price, className }, ref) {
    const { t } = useTranslation(['Common'])

    const isCatalogControllingDiscount = useSelector(
      getFeatureToggle(FeatureToggle.DISCOUNT_ALLOWED_PROCEDURE),
    )

    const { fields, validate, reset } = useFields(
      [
        {
          name: 'discountAllowed',
          label: t('Common:ALLOW_DISCOUNT'),
          type: 'toggle',
          initialValue: price.discountAllowed,
        },
        {
          name: 'taxable',
          label: t('Common:TAXABLE'),
          type: 'toggle',
          initialValue: price.taxable,
        },
        {
          name: 'taxes',
          label: t('Common:SELECT_TAX_RATE'),
          type: 'select',
          initialValue: price.taxes ?? [],
        },
        {
          name: 'active',
          type: 'toggle',
          initialValue: price.active,
        },
      ],
      false,
    )

    const { discountAllowed, taxable, taxes, active } = fields

    useImperativeHandle(ref, () => ({
      get: () => ({
        ...price,
        discountAllowed: discountAllowed.value,
        taxable: taxable.value,
        taxes: taxes.value,
        active: active.value,
      }),
      validate,
      hasUnsavedChanges: () => isFieldValuesChanged(fields),
    }))

    useEffect(() => {
      reset()
    }, [price])

    const priceInfo = [
      { name: t('Common:PRICE_NAME'), value: price.name },
      {
        name: t('Common:SPECIES'),
        value: LanguageUtils.getConstantTranslatedName(
          price.speciesId,
          availableSpecies,
        ),
      },
      {
        name: t('Common:PRICE'),
        value: NumberUtils.formatMoney(price.price ?? 0),
      },
      {
        name: t('Common:MINIMUM_CHARGE'),
        value: NumberUtils.formatMoney(price.minCharge ?? 0),
      },
      {
        name: t('Common:UNITS'),
        value: useGetFormattedPriceUnit(price as Price) as string,
      },
      { name: t('Common:CODE'), value: price.originalCode || '' },
    ]

    return (
      <>
        <Grid
          item
          borderBottom={(theme) => theme.constants.tabBorder}
          pl={2}
          sx={{ display: { xs: 'block', md: 'none' } }}
        >
          <ActiveStateSwitch field={active} />
        </Grid>
        <Grid container className={className} direction="column" wrap="nowrap">
          <InfoList items={priceInfo} />
          <Grid container item direction="column" mt={{ xs: 2, md: 4 }}>
            <Grid item p={0}>
              <PuiSwitch
                disabled={isCatalogControllingDiscount}
                field={discountAllowed}
                label={
                  isCatalogControllingDiscount ? (
                    <TextWithTooltip
                      tooltipText={t(
                        'Tooltips:DISCOUNT_CONTROLLED_BY_CATALOG_TEAM',
                      )}
                    >
                      {discountAllowed.label}
                    </TextWithTooltip>
                  ) : (
                    discountAllowed.label
                  )
                }
              />
            </Grid>
            <Grid
              container
              item
              alignItems="flex-end"
              columnSpacing={1}
              mt={1}
              wrap="nowrap"
            >
              <Grid item>
                <PuiSwitch field={taxable} label={taxable.label} />
              </Grid>
              {taxable.value && (
                <Grid item width={300}>
                  <TaxRateSelect field={taxes} taxableField={taxable} />
                </Grid>
              )}
            </Grid>
            <Grid item sx={{ display: { xs: 'none', md: 'block' } }}>
              <ActiveStateSwitch field={active} />
            </Grid>
          </Grid>
        </Grid>
      </>
    )
  },
)

export default ReadOnlyPrice
